import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import {
  DistributionBarCard,
  type DistributionBarItem,
} from '@/modules/analytics/components/DistributionBarCard/DistributionBarCard';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { AbsoluteChangeIndicator } from './AbsoluteChangeIndicator';
import { useSentimentDistribution } from './hooks/useAiAgentsDistributions';
import { getComparisonPeriodKey } from './PercentChangeIndicator';

const POSITIVE_DELTA_PRECISION = 1;

type UserSentimentCardProps = { className?: string };

export const UserSentimentCard = ({ className }: UserSentimentCardProps) => {
  const { t } = useTranslation();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { isComparing } = useComparisonMode();
  const [isExporting, setIsExporting] = useState(false);

  const { current, previous } = useSentimentDistribution();

  const items = useMemo<DistributionBarItem[]>(
    () => [
      {
        id: 'negative',
        label: t('analytics.negative'),
        value: current.negative,
        previousValue: isComparing ? previous.negative : undefined,
        barClassName: 'bg-chart-2',
      },
      {
        id: 'neutral',
        label: t('analytics.neutral'),
        value: current.neutral,
        previousValue: isComparing ? previous.neutral : undefined,
        barClassName: 'bg-chart-4',
      },
      {
        id: 'positive',
        label: t('analytics.positive'),
        value: current.positive,
        previousValue: isComparing ? previous.positive : undefined,
        barClassName: 'bg-chart-3',
      },
    ],
    [current, previous, isComparing, t]
  );

  const handleExport = useCallback(
    (exportFormat: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename('user_sentiment');
        const rows = items.map(({ id, label, value }) => ({ id, label, percentage: value }));

        if (exportFormat === 'json') {
          downloadJson({ user_sentiment: rows }, filename);
        } else {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [items, t]
  );

  return (
    <DistributionBarCard
      className={className}
      title={t('analytics.fields.user_sentiment')}
      titleTooltip={t('analytics.fields.user_sentiment_tooltip')}
      description={t('analytics.classified_conversations', { count: current.totalCount })}
      items={items}
      isEmpty={current.totalCount === 0}
      exportConfig={{ onExport: handleExport, isExporting }}
      barSizing="fill"
      barRadiusClassName="rounded-md"
      showValueAboveBar
      emptyState={{
        title: t('analytics.no_data_available'),
        description: t('analytics.no_data_for_period'),
      }}
      footer={
        isComparing ? (
          <AbsoluteChangeIndicator
            current={current.positive}
            previous={previous.positive}
            formatDelta={(delta) => {
              const abs = Math.abs(delta).toFixed(1);
              if (delta > 0) {
                return t('analytics.compare_more_positive', { 0: abs });
              }
              if (delta < 0) {
                return t('analytics.compare_less_positive', { 0: abs });
              }
              return t('analytics.compare_unchanged_positive');
            }}
            precision={POSITIVE_DELTA_PRECISION}
            comparisonLabel={t(getComparisonPeriodKey(startDate, endDate))}
          />
        ) : null
      }
    />
  );
};
